import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import type { ElementBoxAttrs } from './elementRenderContext';
import './elementFittedLine.css';

// Words set on one line exactly as wide as their box, the way Kiln sets its name across the foot of every page, but never
// taller than most of the screen, so the whole line is read at once under a sticky header on a wide screen.
// The size is guessed from the words' length while the page is drawn on the server, so the first paint is close
// and the same in the browser, then measured once the browser has the font, and kept as the box changes width.

/** How many ems wide a line is guessed to be per character before it is measured: wide enough never to overflow. */
const EMS_PER_CHARACTER = 0.62;

/** The share of the box the measured line is set to, leaving a hair so rounding never pushes it over. */
const FILL = 0.99;

/** The tallest a line is set, as a share of the screen's height: a wide screen keeps it in view rather than wider. */
const MOST_OF_SCREEN = '85svh';

// Guesses the width of a line in ems from how many characters it holds.
export const guessedEms = (text: string): number => Math.max(1, Array.from(text).length * EMS_PER_CHARACTER);

// Sizes a line so its width in ems spans the whole box (the size container, so 100cqi), short of most of the screen's height.
export const fittedSize = (ems: number): string => `min(calc(100cqi * ${FILL} / ${Number(ems.toFixed(4))}), ${MOST_OF_SCREEN})`;

// Measures the drawn words in ems once the page's fonts are in, again when the words change or a late font lands.
function useMeasuredEms(line: RefObject<HTMLParagraphElement | null>, text: string): number | null {
  const [ems, setEms] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    // 1. The words' own width, read off a range so the paragraph's box width plays no part.
    const measure = () => {
      const node = line.current;
      const size = node ? Number.parseFloat(getComputedStyle(node).fontSize) : 0;
      if (!live || !node || size <= 0) return;
      const range = document.createRange();
      range.selectNodeContents(node);
      const width = range.getBoundingClientRect().width;
      if (width > 0) setEms(width / size);
    };
    // 2. Measured when the fonts are in, and again whenever another face finishes loading.
    void document.fonts.ready.then(measure);
    document.fonts.addEventListener('loadingdone', measure);
    return () => { live = false; document.fonts.removeEventListener('loadingdone', measure); };
  }, [line, text]);
  return ems;
}

// Draws words on one line that fills the width of the box it sits in.
export function FittedLine({ box, dir, text }: { box: ElementBoxAttrs; dir: string; text: string }) {
  const line = useRef<HTMLParagraphElement>(null);
  const ems = useMeasuredEms(line, text) ?? guessedEms(text);
  const style: CSSProperties = { fontSize: fittedSize(ems), whiteSpace: 'nowrap' };
  return (
    <div className="xe-fit">
      <p {...box} ref={line} dir={dir} style={style}>{text}</p>
    </div>
  );
}
